/**
 * **Add a receipt** — the office's, on Money (`docs/visit-page.md` §12,
 * §27.4; the phone's green *Add receipt*). No board draws it: the fields are
 * the ones the crew app sends (§12: *the photo, vendor, amount, day, paid
 * with, against*), labelled in Board 8's own words (lead round 1, item 6) —
 * a DEVIATION REQUEST for the sheet itself.
 *
 * → `POST /visits/:id/receipts`, `CrewPort.addReceipt`'s stand-in: a
 * `field_receipts` row with `entered_by`, posted at once and *To review*
 * (D11), exactly as a crew receipt is.
 */
import { useRef, useState } from 'react'
import type { AddReceiptInput, MoneyRead, PaidWith, VisitRead } from '@rowted/shared'
import { PAID_WITH } from '@rowted/shared'
import { upload } from '../lib/api.js'
import { EM_DASH } from '../lib/format.js'
import { Switch } from '../components/primitives.js'
import { COMMON, PHONE, RECEIPTS } from './copy.js'
import { dayText } from './format.js'
import { Face } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { useVisitWrite, type VisitWriteResult } from './api.js'
import { VisitButton, VisitCard, VisitTile, VISIT_LAYER, VISIT_LAYER_OVER } from './sheet.js'
import { ChoiceField, FieldLabel, instantAt, parseClock, TimeBox } from './time-money-parts.js'

const AMOUNT = /^\d+(\.\d{1,2})?$/
const NONE = 'none'

const box = 'block w-full min-w-0 rounded-button border border-line bg-surface px-[11px] tabular-nums text-text outline-none '
  + 'focus:border-accent focus:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'

export function AddReceiptSheet({ visitId, read, money, phone, onClose, onAdded }: {
  visitId: string
  read: VisitRead
  money: MoneyRead
  phone: boolean
  onClose: () => void
  onAdded: (result: VisitWriteResult) => void
}) {
  const write = useVisitWrite(visitId)
  const days = read.overview.days.days.map((tile) => tile.day)
  const today = days.find((day) => day.relative === 'today') ?? days[0] ?? null
  const [photo, setPhoto] = useState<{ id: string; url: string; name: string } | null>(null)
  const [uploading, setUploading] = useState(false)
  const [vendor, setVendor] = useState('')
  const [amount, setAmount] = useState('')
  const [day, setDay] = useState<string>(today ? String(today.index) : '')
  const [when, setWhen] = useState('')
  const [sentBy, setSentBy] = useState<string | null>(null)
  const [what, setWhat] = useState('')
  const [paidWith, setPaidWith] = useState<PaidWith>('company_card')
  const [against, setAgainst] = useState<string>(NONE)
  const [payBack, setPayBack] = useState(true)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  const dayRef = days.find((candidate) => String(candidate.index) === day) ?? null
  const person = money.receipts.people.find((candidate) => candidate.id === sentBy) ?? null
  const minute = when.trim() ? parseClock(when) : null
  const ready = vendor.trim().length > 0 && AMOUNT.test(amount.trim()) && dayRef !== null && what.trim().length > 0
    && (when.trim() === '' || minute !== null) && !busy && !uploading

  async function pick(file: File | undefined) {
    if (!file) return
    setUploading(true)
    setProblem(null)
    try {
      const sent = await upload(file, { kind: 'receipt', orgUnitId: read.orgUnitId })
      setPhoto({ id: sent.id, url: sent.url, name: sent.name })
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setUploading(false)
    }
  }

  async function add() {
    if (!ready || !dayRef) return
    setBusy(true)
    setProblem(null)
    const line = money.receipts.against.find((option) => `${option.kind}:${option.id}` === against) ?? null
    const body: AddReceiptInput = {
      fileId: photo?.id ?? null,
      vendorName: vendor.trim(),
      sentById: person?.id ?? null,
      amount: amount.trim(),
      day: dayRef.index,
      at: minute !== null ? instantAt(read.timeZone, dayRef.date, minute) : null,
      description: what.trim(),
      paidWith,
      against: line ? { kind: line.kind, id: line.id } : null,
      reimburse: paidWith === 'own_money' && person !== null ? payBack : false,
    }
    try {
      onAdded(await write('POST', '/receipts', body, { tab: 'money' }))
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
      setBusy(false)
    }
  }

  const paidWords: Record<PaidWith, string> = {
    company_card: RECEIPTS.paid.company_card,
    own_money: RECEIPTS.paid.own_money,
    account: RECEIPTS.paid.account,
  }
  const size = phone ? 'phone' : 'card'

  return (
    <VisitCard
      width={520}
      phone={phone}
      title={RECEIPTS.add}
      sub={COMMON.dot(read.reference, read.header.name)}
      mark={<VisitTile glyph="receipt" tone="accent" size={36} />}
      onClose={onClose}
      layer={VISIT_LAYER}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" disabled={!ready} onClick={() => void add()}>{RECEIPTS.add}</VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <FieldLabel>{PHONE.photo}</FieldLabel>
          <button
            type="button"
            onClick={() => picker.current?.click()}
            className="inline-flex h-[120px] w-[90px] items-center justify-center overflow-hidden rounded-[10px] border border-dashed
                       border-line-strong bg-surface-alt text-text-faint hover:border-accent-line"
            aria-label={PHONE.photo}
          >
            {photo
              ? <img src={photo.url} alt={photo.name} className="h-full w-full object-cover" />
              : <VisitGlyph name="receipt" size={22} stroke={1.8} />}
          </button>
          <input ref={picker} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(event) => void pick(event.target.files?.[0])} />
        </div>

        <div className={phone ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-x-3.5 gap-y-3'}>
          <div className="min-w-0">
            <FieldLabel htmlFor="receipt-vendor" required>{COMMON.vendor}</FieldLabel>
            <input id="receipt-vendor" value={vendor} onChange={(event) => setVendor(event.target.value)}
              className={`${box} ${phone ? 'h-11 text-[15px]' : 'h-9 text-[13px]'}`} />
          </div>
          <div className="min-w-0">
            <FieldLabel htmlFor="receipt-amount" required>{RECEIPTS.amount}</FieldLabel>
            <input id="receipt-amount" value={amount} inputMode="decimal" onChange={(event) => setAmount(event.target.value)}
              className={`${box} ${phone ? 'h-11 text-[15px]' : 'h-9 text-[13px]'}`} />
          </div>
          <ChoiceField
            label={COMMON.day}
            required
            value={day}
            choices={days.map((candidate) => ({ value: String(candidate.index), label: COMMON.dayDate(String(candidate.index), dayText(candidate.date, 'date')) }))}
            onChange={setDay}
            phone={phone}
            layer={VISIT_LAYER_OVER}
          />
          <div className="min-w-0">
            <FieldLabel htmlFor="receipt-when">{RECEIPTS.when}</FieldLabel>
            <TimeBox id="receipt-when" label={RECEIPTS.when} value={when} onChange={setWhen} height={36} phone={phone} />
          </div>
          <ChoiceField
            label={RECEIPTS.sentBy}
            value={sentBy}
            choices={money.receipts.people.map((candidate) => ({
              value: candidate.id, label: candidate.name, lead: <Face person={candidate} size={22} />,
            }))}
            onChange={setSentBy}
            phone={phone}
            layer={VISIT_LAYER_OVER}
          />
          <ChoiceField
            label={RECEIPTS.paidWith}
            required
            value={paidWith}
            choices={PAID_WITH.map((value) => ({ value, label: paidWords[value] }))}
            onChange={setPaidWith}
            phone={phone}
            layer={VISIT_LAYER_OVER}
          />
        </div>

        <div className="min-w-0">
          <FieldLabel htmlFor="receipt-for" required>{RECEIPTS.for}</FieldLabel>
          <textarea id="receipt-for" value={what} onChange={(event) => setWhat(event.target.value)}
            className={`${box} h-[52px] resize-none py-2.5 leading-normal ${phone ? 'text-[15px]' : 'text-[13px]'}`} />
        </div>

        <ChoiceField
          label={RECEIPTS.against}
          value={against}
          choices={[
            ...money.receipts.against.map((option) => ({ value: `${option.kind}:${option.id}`, label: option.name })),
            { value: NONE, label: EM_DASH },
          ]}
          onChange={setAgainst}
          phone={phone}
          layer={VISIT_LAYER_OVER}
        />

        {paidWith === 'own_money' && person && (
          <button type="button" role="switch" aria-checked={payBack} onClick={() => setPayBack((was) => !was)}
            className="flex min-h-11 items-center gap-2.5 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-[9px] text-left">
            <Switch checked={payBack} onChange={setPayBack} label={RECEIPTS.payBack(person.firstName)} hideLabel readOnly />
            <span className="text-[12.5px]"><b>{RECEIPTS.payBack(person.firstName)}</b> {RECEIPTS.payBackSub}</span>
          </button>
        )}

        {problem && <p className="m-0 text-[12.5px] text-danger">{problem}</p>}
      </div>
    </VisitCard>
  )
}
